/*
 * Copyright(c) 1995 - 2020 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */

import { Directive, ElementRef, Host, HostBinding, Input, OnDestroy, OnInit, Optional, Renderer2 } from '@angular/core';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { GridRowDirective } from './grid-row.directive';

const GRID_SIZE = 12;

/**
 * A column directive in grid system.
 * Grid column uses display width breakpoints: small - [commonColSm], medium - [commonColMd], large - [commonColLg],
 * and extra large - [commonColXl].
 * Depending on breakpoint, columns will be displayed on an one line or it brakes down respectively,
 * meaning [commonColMd] applies to medium, large and extra large
 * screens, but not to the small. It may be useful when you want to show multiple columns only
 * for bigger displays, but only one column for mobile device.
 *
 * Each selector ([commonCol],[commonColSm],[commonColMd],[commonColLg],[commonColXl]) takes a number,
 * which represents the number of column, which it should take. One row handles 12 columns.
 * Using selector without the number of column means automatically adjust the width of the column.
 *
 * Usage: @see {@link GridModule}
 * Note: the actual size of breakpoints defined in CSS.
 */
@Directive({
    selector: '[commonCol],[commonColSm],[commonColMd],[commonColLg],[commonColXl]'
})
export class GridColDirective implements OnInit, OnDestroy {

    @HostBinding('class.common-grid-col') colClass = true;

    /** Column size without breakpoint. Can accept an empty string in order to support the syntax: <div commonCol></div> */
    @Input('commonCol')
    set column(col: number | '') {
        this.column$.next(col || null);
    }

    /** Columns size for small breakpoint. Can accept an empty string in order to support the syntax <div commonColSm></div> */
    @Input('commonColSm')
    set columnSm(col: number | '') {
        this.columnSm$.next(col || null);
    }

    /** Columns size for medium breakpoint. Can accept an empty string in order to support the syntax <div commonColMd></div> */
    @Input('commonColMd')
    set columnMd(col: number | '') {
        this.columnMd$.next(col || null);
    }

    /** Columns size for large breakpoint. Can accept an empty string in order to support the syntax <div commonColLg></div> */
    @Input('commonColLg')
    set columnLg(col: number | '') {
        this.columnLg$.next(col || null);
    }

    /** Columns size for extra large breakpoint. Can accept an empty string in order to support the syntax <div commonColXl></div> */
    @Input('commonColXl')
    set columnXl(col: number | '') {
        this.columnXl$.next(col || null);
    }

    private subscription = new Subscription();

    private column$ = new BehaviorSubject<number | null | undefined>(undefined);

    private columnSm$ = new BehaviorSubject<number | null | undefined>(undefined);

    private columnMd$ = new BehaviorSubject<number | null | undefined>(undefined);

    private columnLg$ = new BehaviorSubject<number | null | undefined>(undefined);

    private columnXl$ = new BehaviorSubject<number | null | undefined>(undefined);

    constructor(
        private renderer: Renderer2,
        private el: ElementRef,
        @Optional() @Host() rowDirective: GridRowDirective
    ) {
        if (rowDirective === null) {
            throw Error('Directive "commonRow" is required. Usage: <div commonRow> <div commonCol></div></div>');
        }
    }

    ngOnInit(): void {
        this.subscription.add(
            combineLatest([this.column$, this.columnSm$, this.columnMd$, this.columnLg$, this.columnXl$])
                .subscribe(([col, sm, md, lg, xl]) => {
                    this.removeOldCssClasses();
                    this.addCssClasses(col, { sm, md, lg, xl });
                })
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.column$.complete();
        this.columnSm$.complete();
        this.columnMd$.complete();
        this.columnLg$.complete();
        this.columnXl$.complete();
    }

    private addCssClasses(columnSize: number | null | undefined, breakpoints: BreakpointValues): void {
        ['sm', 'md', 'lg', 'xl']
            .filter((size: BootstrapBreakpoint) => breakpoints[size] !== undefined)
            .forEach((size: BootstrapBreakpoint) => this.addColumnBreakpointCssClass(size, breakpoints[size]));

        if (columnSize !== undefined) {
            this.addColumnCssClass(columnSize);
        }
    }

    private addColumnBreakpointCssClass(size: BootstrapBreakpoint, col: number | null): void {
        const cssClass = col === null ? `col-${size}` : `col-${size}-${col}`;
        this.renderer.addClass(this.el.nativeElement, cssClass);
    }

    private addColumnCssClass(size: number | null): void {
        const cssClass = size === null ? 'col' : `col-${size}`;
        this.renderer.addClass(this.el.nativeElement, cssClass);
    }

    private removeOldCssClasses(): void {
        this.renderer.removeClass(this.el.nativeElement, 'col');
        for (let col = 1; col <= GRID_SIZE; col++) {
            this.renderer.removeClass(this.el.nativeElement, `col-${col}`);
        }

        ['sm', 'md', 'lg', 'xl'].forEach(size => {
            for (let col = 1; col <= GRID_SIZE; col++) {
                this.renderer.removeClass(this.el.nativeElement, `col-${size}-${col}`);
            }
        })
    }
}

type BootstrapBreakpoint = 'sm' | 'md' | 'lg' | 'xl';

type BreakpointValues = {
    sm: number | null,
    md: number | null,
    lg: number | null,
    xl: number | null
}
